import {
  foodBaseAfterDiscount,
  invoiceChargeRates,
  roundMoney,
  type InvoiceChargeRates,
} from "./pricing";
import { liveOpenTotals } from "./groupPlates";
import {
  allocateInvoiceRefundShares,
  refundableItems,
  type RefundAllocationInvoice,
} from "./refundAllocation";
import type { Invoice, InvoiceItem } from "@/lib/api/types";

/**
 * SPLITTING A BILL BY WHO ORDERED WHAT (B43), and what is still owed on a bill
 * paid in pieces (B3) — the pure decisions behind the two till screens.
 *
 * The founder's answer to "a table of ten, three families, each pays for what
 * they ordered" is that a family pays for THE PLATES IT ORDERED. So the split
 * is not an arithmetic the till invents; it is a MARKING on plates, and the
 * amount falls out of the marking.
 *
 * **THE ARITHMETIC IS NOT NEW AND MUST NOT BE.** "What does this set of plates
 * owe, service charge and VAT included" is exactly the question
 * `allocateInvoiceRefundShares` already answers, to the piastre, telescoping to
 * the bill's own stored totals — and the server's `amountOwedByParty` derives a
 * party settlement through that same function for that same reason. A second
 * implementation of it here is the divergence the money helpers were unified to
 * remove, so this module REUSES it and only selects out of the result.
 *
 * **THE BASIS IS THE WHOLE CHARGEABLE SET, never one party's plates.** Passing
 * a subset destroys the allocation's telescoping property (its own header says
 * so in those words): three families' shares computed off three separate bases
 * would each be right on its own and would not add up to the bill. The whole
 * set goes in, the subset comes out — the identical rule the refund sheet
 * follows, and for the identical reason.
 *
 * **THE TILL STILL SENDS NO AMOUNT.** `settlePartial` with a `partyId` carries
 * no figure at all; the server derives it. Everything here exists so the
 * CASHIER can read what a family will be asked for before the command goes out,
 * which is trap 1 — the rule on screen, in numbers, not in a comment.
 */

/** One party's share of the bill, ready to render. */
export type PartyShare = {
  id: string;
  label: string;
  /** How many chargeable plates carry this mark. */
  itemCount: number;
  /**
   * What this party's plates come to — their share of the subtotal, the
   * service charge and VAT. `null` when the till cannot state the split (see
   * `allocateInvoiceRefundShares`'s degradation); the server remains the
   * authority either way.
   *
   * This is the family's WHOLE share and not what it still owes. `outstanding`
   * below is the figure a tender is priced at.
   */
  amount: number | null;
  /**
   * What this family has ALREADY handed over, from the server's money ledger.
   * `0` on every party nobody has taken money for, which is every party on a
   * bill still waiting to be paid.
   */
  paid: number;
  /**
   * What is still to come from this family — `amount − paid`, floored at zero,
   * and `null` for the same reason `amount` is.
   *
   * THIS is what the next tender takes, and the server derives exactly the same
   * subtraction (`settlePartialInTx`). Without it the till quoted a family's
   * whole share however much of it was already in the drawer, and taking it
   * again was one tap with nothing in the way.
   */
  outstanding: number | null;
  /** Nothing left to take from this family. False whenever the till cannot say. */
  isSettled: boolean;
};

/**
 * The plates that may carry a mark: the bill's CHARGEABLE set.
 *
 * The same set the refund sheet allocates over, and the same one the server's
 * `amountOwedByParty` filters to. A voided plate is off the bill and a comped
 * one was never charged for, so neither has a share for a family to pay — and
 * marking one would let a waiter appear to give a family plates that cost
 * nothing while the arithmetic ignored them.
 */
export function markablePlates(invoice: Invoice): InvoiceItem[] {
  return refundableItems(invoice);
}

/**
 * Every party on this bill with what it owes, in the order the server returns
 * them (creation order), so chips do not reshuffle under a thumb.
 *
 * A party with no chargeable plates left is KEPT, showing `0.00` — it is a
 * label a waiter created and may be about to mark plates onto, and a chip that
 * vanished the moment its last plate was unmarked would read as the undo having
 * deleted the party.
 */
export function partyShares(invoice: Invoice): PartyShare[] {
  const plates = markablePlates(invoice);
  const shares = allocateInvoiceRefundShares(
    allocationBasis(invoice),
    plates.map((item) => ({ id: item.id, lineTotal: item.lineTotal })),
  );

  return invoice.parties.map((party) => {
    const mine = plates.filter((item) => item.partyId === party.id);
    const amount = shares
      ? mine.reduce(
          (sum, item) => roundMoney(sum + (shares.get(item.id)?.amount ?? 0)),
          0,
        )
      : null;
    const paid = party.tenderedTotal;
    // Floored at zero: a family already over-paid owes nothing more, and a
    // negative "still to come" is a number a cashier will try to act on.
    const outstanding =
      amount === null ? null : Math.max(0, roundMoney(amount - paid));
    return {
      id: party.id,
      label: party.label,
      itemCount: mine.length,
      amount,
      paid,
      outstanding,
      // `null` is "the till cannot say", which is not the same claim as
      // "settled" and must never render as one.
      isSettled: outstanding !== null && outstanding === 0 && paid > 0,
    };
  });
}

/**
 * THE FIGURES A SHARE IS ALLOCATED OUT OF — stored on a finished bill, LIVE on
 * an open one.
 *
 * The stored columns are written at CLOSE. On an OPEN bill they are all zero,
 * so `allocateInvoiceRefundShares` met `basis <= 0`, degraded to `null`, and
 * every family on the split screen rendered its share as an em dash — for the
 * whole of the time a waiter is actually marking plates, which is the only time
 * that screen is used. The sheet's own header promises the opposite: "what each
 * family will be asked for is QUOTED here".
 *
 * `liveOpenTotals` is the SAME helper the order panel already prices an open
 * bill's subtotal, service charge and VAT from, so the split screen and the
 * panel beside it cannot disagree about what the bill comes to. On a CLOSED or
 * SETTLED bill the stored columns are the authority and are used unchanged —
 * a reopened bill's plates may have moved since, and the allocation must
 * telescope to the figures the cheque was printed with.
 *
 * Exported for the PAPER settle sheet's "by item" quote (`settleSheet.ts`),
 * which prices a would-be party off this same basis rather than a second one.
 */
export function allocationBasis(invoice: Invoice): RefundAllocationInvoice {
  const rates = invoiceChargeRates(invoice);
  if (invoice.status === "OPEN") {
    const live = liveOpenTotals(invoice);
    return withDiscountedBase(
      {
        subtotal: live.subtotal,
        serviceChargeTotal: live.serviceChargeTotal,
        vatTotal: live.vatTotal,
        pricesIncludeTax: invoice.pricesIncludeTax,
      },
      // D146: the live discount — a PERCENT tracks the plates.
      live.billDiscountTotal,
      rates,
    );
  }
  return withDiscountedBase(
    {
      subtotal: invoice.subtotal,
      serviceChargeTotal: invoice.serviceChargeTotal,
      vatTotal: invoice.vatTotal,
      pricesIncludeTax: invoice.pricesIncludeTax,
    },
    invoice.billDiscount?.amount ?? 0,
    rates,
  );
}

/**
 * D146 — the server's one pro-rating rule for plate money on a discounted
 * bill: every share is allocated off the food AFTER the discount.
 */
function withDiscountedBase(
  basis: RefundAllocationInvoice,
  discountNet: number,
  rates: InvoiceChargeRates,
): RefundAllocationInvoice {
  if (!(discountNet > 0)) return basis;
  return {
    ...basis,
    discountedSubtotal: foodBaseAfterDiscount(
      basis.subtotal,
      rates,
      discountNet,
    ),
  };
}

/**
 * The plates NOBODY has claimed — and they are not a party.
 *
 * Unmarked is "nobody has been asked", exactly as a null `guestCount` is: it is
 * NOT a default party and no figure may treat it as one. It is reported
 * separately so a cashier can see, before taking the last family's money, that
 * a plate is still unaccounted for — the failure mode of this whole screen is a
 * table that leaves with one dish nobody paid for.
 */
export function unmarkedPlates(invoice: Invoice): InvoiceItem[] {
  return markablePlates(invoice).filter((item) => item.partyId === null);
}

/**
 * WHAT THE UNCLAIMED PLATES COME TO — their share of the bill, charges and all.
 *
 * The COUNT of unmarked plates was already said on the split screen; the
 * FIGURE is what makes it actionable at the till, where the question is not
 * "are any unmarked" but "how much of this bill is on nobody". Allocated out of
 * the same whole-set basis every other share here is, so the families' shares
 * and this one sum to the bill exactly.
 *
 * `null` when the till cannot state the split, on the same terms as
 * `PartyShare.amount`, and `0` on a bill whose every plate is claimed.
 */
export function unclaimedValueOnBill(invoice: Invoice): number | null {
  const plates = markablePlates(invoice);
  const shares = allocateInvoiceRefundShares(
    allocationBasis(invoice),
    plates.map((item) => ({ id: item.id, lineTotal: item.lineTotal })),
  );
  if (!shares) return null;
  return plates
    .filter((item) => item.partyId === null)
    .reduce(
      (sum, item) => roundMoney(sum + (shares.get(item.id)?.amount ?? 0)),
      0,
    );
}

/**
 * WHAT IS STILL OWED on a bill being paid in pieces (B3).
 *
 * `total - tenderedTotal`, off the ledger's own sum — never off
 * `settlementAmount`, which describes the bill as a whole and carries the LAST
 * method taken.
 *
 * FLOORED AT ZERO. An over-tender cannot happen through this screen (the server
 * refuses one), but a bill read mid-flight while another till takes the rest
 * can arrive here already square, and a negative "still owed" rendered on a
 * till is a number a cashier will try to act on.
 */
export function outstandingOnBill(invoice: Invoice): number {
  return Math.max(0, roundMoney(billTotal(invoice) - invoice.tenderedTotal));
}

/**
 * The bill's total as it stands. An OPEN bill's stored `total` is not written
 * until the close, and since D126 an open bill can carry money — so reading it
 * there compared 248.98 paid against 0 and told the cashier they owed the guest
 * the lot. On OPEN it is the live total the foot shows.
 */
function billTotal(invoice: Invoice): number {
  return invoice.status === "OPEN" ? liveOpenTotals(invoice).total : invoice.total;
}

/** Whether this bill has had money taken against it without being settled. */
export function isPartlyTendered(invoice: Invoice): boolean {
  return invoice.tenderedTotal > 0 && invoice.status !== "SETTLED";
}

/**
 * WHAT THE BILL OWES THE CUSTOMER (B89) — the mirror of `outstandingOnBill`.
 *
 * A bill can be part-paid and THEN shortened: 300 taken of 500, a plate the
 * family never got voided, re-closed at 100. The restaurant is holding 200
 * nobody owes it, and until this existed the till had no way even to SAY so —
 * the outstanding balance floored at zero, which reads as "square" and is not.
 *
 * `0` on every ordinary bill, which is what keeps it out of the way: a "0.00
 * owed back" line on every screen would teach a cashier to stop reading the one
 * line that matters when it is not zero.
 */
export function overpaidOnBill(invoice: Invoice): number {
  return Math.max(0, roundMoney(invoice.tenderedTotal - billTotal(invoice)));
}

/**
 * What one `assignParty` command carries.
 *
 * THREE EDITS, ONE COMMAND — the server's own shape, mirrored here so the
 * tri-state is decided in one place with one test rather than at three call
 * sites in a dialog:
 *
 * · `{ mode: "new", label }` CREATES the party and marks the plates onto it.
 * · `{ mode: "existing", partyId }` marks them onto one that exists.
 * · `{ mode: "clear" }` sends `partyId: null`, which is the undo.
 *
 * `null` and ABSENT are different answers on this wire and the difference is
 * load-bearing: absent means "create from the label", so a "clear" that dropped
 * the key would be rejected as a missing label rather than clearing anything.
 */
export type AssignPartyInput =
  | { mode: "new"; label: string; itemIds: readonly string[] }
  | { mode: "existing"; partyId: string; itemIds: readonly string[] }
  | { mode: "clear"; itemIds: readonly string[] };

export function resolveAssignPartyPayload(
  input: AssignPartyInput,
): Record<string, unknown> | null {
  if (input.itemIds.length === 0) {
    // Nothing ticked is not an empty instruction — it is no instruction. The
    // command would be applied, write nothing, and still take an id out of the
    // idempotency log.
    return null;
  }
  const itemIds = [...input.itemIds];
  if (input.mode === "clear") {
    // EXPLICIT null. Not an omitted key: see the type's note.
    return { itemIds, partyId: null };
  }
  if (input.mode === "existing") {
    return { itemIds, partyId: input.partyId };
  }
  const label = input.label.trim();
  // A whitespace-only label is the same hole the aggregator form had: it passes
  // a bare "is it there" check and fails at the server as a required-label
  // refusal after the waiter has already ticked six plates.
  return label ? { itemIds, label } : null;
}
